// Slide-over create/edit forms for Season and Competition, matching the Stage/Group
// slide-overs. They reuse the exact same APIs and payloads as the original modals
// (addSeason/updateSeason, createCompetition/updateCompetition) — only the chrome
// changed, so behaviour (logo upload, stats mode, validation) is identical.

import React, { useState, useEffect } from 'react'
import { CircularProgress } from '@material-ui/core'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { SlideOver, Field, TextInput, SelectInput, Button } from '../Primitives'
import TOKENS from '../tokens'
import QueryKeys from '../../../../utils/constants/queryKeys'
import addSeason from '../../../../Apis/season/addSeason'
import updateSeason from '../../../../Apis/season/updateSeason'
import createCompetition from '../../../../Apis/competition/createCompetition'
import getCompetition from '../../../../Apis/competition/getCompetition'
import updateCompetition from '../../../../Apis/competition/updateCompetition'
import getGenderSpec from '../../../../Apis/team/getGenderSpec'
import getAgeRestriction from '../../../../Apis/team/getAgeRestriction'
import STATS_MODE_OPTIONS from '../../../../utils/constants/statsModeOptions'
import ImageCrop from '../../../../components/ui/ImageCropComponents/ImageCrop/ImageCrop'
import CROPPER_SHAPES from '../../../../utils/constants/cropperShapes'

const pad = (n) => String(n).padStart(2, '0')
const toInputDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/* ------------------------------------------------------------ Season form -- */

export const SeasonSlideOver = ({ open, onClose, orgId, competitionId, season, onCreated }) => {
  const isEdit = Boolean(season)
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [statsMode, setStatsMode] = useState('Full')
  // null = untouched (not sent), '' = cleared with the cropper's X (the backend removes it), data URI = new
  const [logo, setLogo] = useState(null)

  useEffect(() => {
    if (open) {
      setName(season?.name || '')
      setStart(toInputDate(season?.startDate))
      setEnd(toInputDate(season?.endDate))
      setStatsMode('Full')
      setLogo(null)
    }
  }, [open, season])

  const invalidate = () => {
    if (competitionId) queryClient.invalidateQueries([QueryKeys.GET_COMPETITION, orgId, competitionId])
    queryClient.invalidateQueries(QueryKeys.GET_SEASONS)
  }
  const createMutation = useMutation((data) => addSeason(data), {
    onSuccess: (response) => {
      invalidate()
      onClose()
      // Jump into the season we just created.
      if (onCreated && response?.data) onCreated(response.data)
    },
  })
  const updateMutation = useMutation((data) => updateSeason(data), {
    onSuccess: () => { invalidate(); onClose() },
  })
  const isLoading = createMutation.isLoading || updateMutation.isLoading

  const datesOutOfOrder = start && end && new Date(end) < new Date(start)
  // Crops land asynchronously; Save waits for them so a zoom made just before is not lost
  const [cropsPending, setCropsPending] = useState(0)
  const trackCrop = (busy) => setCropsPending((n) => n + (busy ? 1 : -1))
  const canSubmit = name.trim() && start && end && !datesOutOfOrder && cropsPending === 0 && !isLoading

  const submit = () => {
    const base = {
      orgId,
      name,
      startDate: start ? new Date(start) : null,
      endDate: end ? new Date(end) : null,
      ...(logo !== null && { logoDataUri: logo }),
    }
    if (isEdit) updateMutation.mutate({ ...base, seasonId: season.id })
    else createMutation.mutate({ ...base, statsMode, ...(competitionId && { competitionId }) })
  }

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Season' : 'New Season'}
      subtitle={isEdit ? 'Update season details and dates.' : 'A season is one edition of a competition (e.g. 2025-2026).'}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {isLoading ? <CircularProgress size={18} style={{ color: TOKENS.white }} /> : isEdit ? 'Update' : 'Create Season'}
          </Button>
        </>
      )}
    >
      <Field label="Season name">
        <TextInput placeholder="e.g. 2025-2026" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Start date"><TextInput type="date" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
        <Field label="End date" hint={datesOutOfOrder ? 'End date must be after start date.' : undefined}>
          <TextInput type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </Field>
      </div>
      {!isEdit && (
        <Field label="Stats mode">
          <SelectInput
            value={statsMode}
            onChange={(e) => setStatsMode(e.target.value)}
            options={STATS_MODE_OPTIONS.map((o) => ({ value: o.id, label: o.name }))}
          />
        </Field>
      )}
      <Field as="div" label="Logo" hint="Optional. A square image works best.">
        <ImageCrop
          defaultImageSrc={season?.logoUrl}
          onBusyChange={trackCrop}
          setCroppedImage={setLogo}
          width={200}
          height={200}
          addImageLabel="Upload season logo"
        />
      </Field>
    </SlideOver>
  )
}

/* ------------------------------------------------------- Competition form -- */

// Edit mode receives the competition's enum *names* (e.g. "U15", "Female"); the
// API expects the numeric enum ids. Same maps the original modal used.
const AGE_RESTRICTION_ENUM_IDS = {
  NoRestriction: 1, U22: 2, U21: 3, U20: 4, U19: 5, U18: 6,
  U17: 7, U16: 8, U15: 9, U14: 10, Junior: 11, Mini: 12, Academy: 13,
}
const GENDER_ENUM_IDS = { Male: 1, Female: 2, NotSpecific: 3 }

export const CompetitionSlideOver = ({ open, onClose, orgId, competition }) => {
  const isEdit = Boolean(competition)
  const queryClient = useQueryClient()
  // Only load the option lists when the slide-over is actually open (it stays mounted).
  const { data: genders = [] } = useQuery([QueryKeys.GET_GENDER_SPEC], () => getGenderSpec(), { enabled: open })
  const { data: ageRestrictions = [] } = useQuery([QueryKeys.GET_AGE_RESTRICTION], () => getAgeRestriction(), { enabled: open })
  // The list rows don't carry the scoresheet logo; the single-competition read does (shared with the page's query)
  const { data: competitionDetail } = useQuery(
    [QueryKeys.GET_COMPETITION, orgId, competition?.id],
    () => getCompetition({ orgId, competitionId: competition.id }),
    { enabled: open && Boolean(competition?.id) },
  )

  const [name, setName] = useState('')
  const [ageRestriction, setAgeRestriction] = useState('')
  const [gender, setGender] = useState('')
  // Image state follows the API's three-state convention: null = untouched (not sent),
  // '' = cleared with the cropper's X (sent, the backend removes it), data URI = new image.
  const [logo, setLogo] = useState(null)
  // Scoresheet logo (DEV-1081): when set, printed on the PDF scoresheet instead of the federation logo
  const [scoresheetLogo, setScoresheetLogo] = useState(null)

  useEffect(() => {
    if (open) {
      setName(competition?.name || '')
      setAgeRestriction(competition ? (AGE_RESTRICTION_ENUM_IDS[competition.ageRestriction] ?? competition.ageRestriction ?? '') : '')
      setGender(competition ? (GENDER_ENUM_IDS[competition.gender] ?? competition.gender ?? '') : '')
      setLogo(null)
      setScoresheetLogo(null)
    }
  }, [open, competition])

  const invalidate = () => {
    queryClient.invalidateQueries([QueryKeys.GET_COMPETITIONS, orgId])
    if (competition?.id) queryClient.invalidateQueries([QueryKeys.GET_COMPETITION, orgId, competition.id])
    onClose()
  }
  const createMutation = useMutation((data) => createCompetition({ orgId, data }), { onSuccess: invalidate })
  const updateMutation = useMutation(({ competitionId, data }) => updateCompetition({ orgId, competitionId, data }), { onSuccess: invalidate })
  const isLoading = createMutation.isLoading || updateMutation.isLoading

  // Crops land asynchronously; Save waits for them so a zoom made just before is not lost
  const [cropsPending, setCropsPending] = useState(0)
  const trackCrop = (busy) => setCropsPending((n) => n + (busy ? 1 : -1))
  const canSubmit = name.trim() && ageRestriction !== '' && gender !== '' && cropsPending === 0 && !isLoading

  const submit = () => {
    const data = {
      orgId,
      name,
      ageRestriction,
      gender,
      ...(logo !== null && { logoDataUri: logo }),
      ...(scoresheetLogo !== null && { scoresheetLogoDataUri: scoresheetLogo }),
    }
    if (isEdit) updateMutation.mutate({ competitionId: competition.id, data })
    else createMutation.mutate(data)
  }

  const ageOptions = [{ value: '', label: 'Select age restriction…' }, ...ageRestrictions.map((o) => ({ value: o.id, label: o.name }))]
  const genderOptions = [{ value: '', label: 'Select gender…' }, ...genders.map((o) => ({ value: o.id, label: o.name }))]

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Competition' : 'New Competition'}
      subtitle={isEdit ? 'Update competition details.' : 'Create a new league, cup or tournament.'}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {isLoading ? <CircularProgress size={18} style={{ color: TOKENS.white }} /> : isEdit ? 'Update' : 'Create Competition'}
          </Button>
        </>
      )}
    >
      <Field label="Competition name">
        <TextInput placeholder="e.g. National Youth Championship" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Age restriction">
          <SelectInput value={ageRestriction} onChange={(e) => setAgeRestriction(Number(e.target.value) || '')} options={ageOptions} />
        </Field>
        <Field label="Gender">
          <SelectInput value={gender} onChange={(e) => setGender(Number(e.target.value) || '')} options={genderOptions} />
        </Field>
      </div>
      <Field as="div" label="Logo" hint="Optional. A square image works best.">
        <ImageCrop
          defaultImageSrc={competition?.logoUrl}
          onBusyChange={trackCrop}
          setCroppedImage={setLogo}
          width={200}
          height={200}
          addImageLabel="Upload competition logo"
        />
      </Field>
      <Field as="div" label="Scoresheet logo" hint="Optional. PNG or JPG.">
        <p style={{ margin: '0 0 8px', fontSize: 12, color: TOKENS.text2 }}>
          Used when the PDF scoresheet is generated: it is printed top-left on the sheet instead of the federation logo.
        </p>
        <ImageCrop
          defaultImageSrc={competitionDetail?.scoresheetLogoUrl}
          onBusyChange={trackCrop}
          setCroppedImage={setScoresheetLogo}
          width={340}
          height={168}
          aspect={150 / 74}
          cropSize={{ width: 300, height: 148 }}
          cropShape={CROPPER_SHAPES.RECT}
          addImageLabel="Upload scoresheet logo"
        />
      </Field>
    </SlideOver>
  )
}
